import type { ApiSchemas } from '@rental/contracts/client'
import type { StaffLocale } from '@rental/domain'

/**
 * S9 (ADM-1, Q-51): what the Persone screens decide from what the server
 * said, kept apart from them so it can be tested alone. The server decides
 * everything that matters again; these only choose what to show and ask.
 */

export type Person = ApiSchemas['StaffPerson']
export type PersonDevice = ApiSchemas['StaffPersonDevice']
export type RoleOption = ApiSchemas['StaffRoleOption']

/** A person's password: their own, one someone else chose that still works, or one that no longer does. */
export type PasswordState = 'own' | 'toChoose' | 'expired'

/**
 * As the server judges it (`refuseExpiredPassword`): a password someone else
 * chose works until its time, and not at it.
 */
export function passwordState(
  person: Pick<Person, 'passwordTemporaryUntil'>,
  now: number,
): PasswordState {
  if (person.passwordTemporaryUntil === null) return 'own'
  return Date.parse(person.passwordTemporaryUntil) > now ? 'toChoose' : 'expired'
}

/**
 * The devices a deactivation offers to sign out: those still signed in, and
 * not the one asking, which is signed out from Dispositivi (the server
 * refuses it here: `device_current`).
 */
export function signOutChoices(devices: readonly PersonDevice[]): PersonDevice[] {
  return devices.filter((device) => !device.current && device.revokedAt === null)
}

/** Ticked from the start: those nobody else has used, a runner's own phone. A shared iPad is not. */
export function preTicked(devices: readonly PersonDevice[]): string[] {
  return signOutChoices(devices)
    .filter((device) => device.onlyThisPerson)
    .map((device) => device.id)
}

/**
 * What a new role does to the authenticator: to a role that signs in with a
 * code, a new key, shown once (the server makes one at every move to such a
 * role); away from one, the key stops working. Null when the role stays.
 */
export function keyChange(
  roles: readonly RoleOption[],
  from: string,
  to: string,
): 'newKey' | 'keyEnds' | null {
  if (from === to) return null
  const withCode = (code: string) => roles.find((role) => role.code === code)?.requiresCode === true
  if (withCode(to)) return 'newKey'
  return withCode(from) ? 'keyEnds' : null
}

export interface PersonDraft {
  displayName: string
  locale: StaffLocale
  role: string
}

export const draftOf = (person: Person): PersonDraft => ({
  displayName: person.displayName,
  locale: person.locale,
  role: person.role,
})

/**
 * Only what changed since the person was read, as the server takes it: the
 * name trimmed, as the server keeps it. A role changed is access given or
 * taken, and takes the Owner's code.
 */
export function personChanges(
  read: Person,
  draft: PersonDraft,
): { displayName?: string; locale?: StaffLocale; role?: string } {
  const name = draft.displayName.trim()
  return {
    ...(name !== read.displayName ? { displayName: name } : {}),
    ...(draft.locale !== read.locale ? { locale: draft.locale } : {}),
    ...(draft.role !== read.role ? { role: draft.role } : {}),
  }
}

/** What the server's PersonCreateRequest takes as an e-mail; it says the rest. */
export const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
